import { useState, useEffect, useMemo } from "react";
import { useMsal } from '@azure/msal-react';
import { Modal, Button } from 'react-bootstrap';
import CalendarApi from '../../api/calendar/calendar-api-factory';
import { CompanyAPI } from "../../api/company/company-api";
import DateTimePicker from "./datetime-picker";
import Comparer from "../../app/comparer";
import moment from "moment";
import { DateTime } from 'luxon';
import Humanizer from "humanize-duration";

const StandardEventModal = (props) => {

    const { instance, inProgress, accounts } = useMsal();

    const [startOpen, setStartOpen] = useState(false);
    const [endOpen, setEndOpen] = useState(false);
    const [calendarEvent, setEvent] = useState({ allDay: true, title: "", description: "", location: "", start: new Date(), end: new Date(), recurrenceDays: [], recurrenceEnd: null });
    const [originalState, setOriginalState] = useState({});
    const [fetchComplete, setFetchComplete] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [isRecurring, setIsRecurring] = useState(false);

    const comparer = new Comparer();

    const calendarApi = useMemo(() => new CalendarApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);
    const companyApi = useMemo(() => new CompanyAPI(instance, inProgress, accounts, false), [instance, inProgress, accounts]);

    useEffect(() => {
        let isMounted = true;

        const loadCompanies = async () => {
            try {
                const result = await companyApi.getAll();
                if (!isMounted) return;

                const list = Array.isArray(result) ? result : [];
                setCompanies(list);

                // For new events with a single company, silently default
                // the event's companyId to that company so it is included
                // in the POST body even though no dropdown is shown.
                if (fetchComplete && props.standardEventId === -1 && list.length === 1) {
                    const companyId = list[0].id;

                    setEvent(prev => {
                        const updated = Object.assign({}, prev);
                        const extended = Object.assign({}, updated.extendedProps);
                        extended.companyId = companyId;
                        updated.extendedProps = extended;
                        return updated;
                    });

                    setOriginalState(prev => {
                        const updated = Object.assign({}, prev);
                        const extended = Object.assign({}, updated.extendedProps);
                        extended.companyId = companyId;
                        updated.extendedProps = extended;
                        return updated;
                    });
                }
            }
            catch (error) {
                console.log("Error loading companies for calendar events.", error);
            }
        };

        loadCompanies();

        return () => {
            isMounted = false;
        };
    }, [companyApi, fetchComplete, props.standardEventId]);

    // Helper to normalise an event (from API or from FullCalendar initialEvent)
    // and derive recurrence UI fields.
    const hydrateEvent = (sourceEvent) => {
        if (!sourceEvent) return { event: null, hasRecurrence: false };

        const event = Object.assign({}, sourceEvent);

        event.start = moment.utc(event.start);
        event.end = moment.utc(event.end ?? event.start);

        const extended = event.extendedProps || {};
        const recurrence = event.recurrence || {};

        const weekdayMap = {
            monday: 'Mon',
            tuesday: 'Tue',
            wednesday: 'Wed',
            thursday: 'Thu',
            friday: 'Fri',
            saturday: 'Sat',
            sunday: 'Sun'
        };

        let sourceByWeekDays = [];
        if (Array.isArray(recurrence.byWeekDays)) {
            sourceByWeekDays = recurrence.byWeekDays;
        } else if (Array.isArray(extended.byWeekDays)) {
            sourceByWeekDays = extended.byWeekDays;
        }

        let recurrenceDays = [];
        if (Array.isArray(sourceByWeekDays)) {
            recurrenceDays = sourceByWeekDays
                .map(d => weekdayMap[(d || '').toString().toLowerCase()])
                .filter(Boolean);
        }

        let untilSource = recurrence.untilUtc || extended.untilUtc;
        let recurrenceEnd = null;
        if (untilSource) {
            recurrenceEnd = moment.utc(untilSource).format("YYYY-MM-DD");
        }

        event.recurrenceDays = recurrenceDays;
        event.recurrenceEnd = recurrenceEnd;

        const hasRecurrence = (recurrenceDays && recurrenceDays.length > 0) || !!recurrenceEnd;

        return { event, hasRecurrence };
    };

    useEffect(() => {
        // Editing an existing event
        if (!fetchComplete && props.standardEventId !== -1) {
            // Prefer the clicked FullCalendar event passed in as initialEvent to
            // avoid 404s when the occurrence itself has no backing record.
            if (props.initialEvent) {
                const { event, hasRecurrence } = hydrateEvent(props.initialEvent);
                setEvent(event);
                setOriginalState(event);
                setIsRecurring(hasRecurrence);
                setFetchComplete(true);
            } else {
                calendarApi.getEventById(props.standardEventId)
                    .then(apiEvent => {
                        const { event, hasRecurrence } = hydrateEvent(apiEvent);
                        setEvent(event);
                        setOriginalState(event);
                        setIsRecurring(hasRecurrence);
                        setFetchComplete(true);
                    });
            }
        }
        // Creating a brand new event
        else if (!fetchComplete) {
            setFetchComplete(true);
            let event = { allDay: true, title: "", description: "", location: "", start: props.newDateSelected, end: props.newDateSelected, recurrenceDays: [], recurrenceEnd: null };

            event.start = moment.utc(event.start);
            event.end = moment.utc(event.end ?? event.start);
            setEvent(event);
            setOriginalState(event);
            setIsRecurring(false);
        }
    }, [fetchComplete, props.newDateSelected, props.standardEventId, props.initialEvent, calendarApi]);

    const canSave = () => {
        const equal = comparer.equals(calendarEvent, originalState);

        // If multiple companies exist, require a company selection when the
        // dropdown is shown.
        const companyRequired = companies.length > 1;
        let hasCompany = true;
        if (companyRequired) {
            const extended = calendarEvent.extendedProps || {};
            hasCompany = !!extended.companyId;
        }

        return !equal && fetchComplete && (!companyRequired || hasCompany);
    }

    const setEndDate = (date) => {

        let newEvent = Object.assign({}, calendarEvent);
        let start = moment.utc(newEvent.start), end = moment.utc(date);

        if (end.diff(start) < 0) { //start is after end, so sort it out
            start = end;
        }

        newEvent.start = start;
        newEvent.end = end;

        setEvent(newEvent);
    };

    const setStartDate = (date) => {

        let selectedDate = moment.utc(Object.assign({}, date));

        let newEvent = Object.assign({}, calendarEvent);

        let start = moment.utc(newEvent.start), end = moment.utc(newEvent.end);
        let diff = end.diff(start, "days");

        newEvent.start = selectedDate;
        end = selectedDate;

        if (start.diff(end) < 0) { //start is after end, so sort it out
            end = moment.utc(end).add(diff, 'days');
        }

        newEvent.end = end;
        setEvent(newEvent);
    }

    const setDateToLastSecondOfDay = (date) => {

        let result = moment.utc(date);

        let year = result.year();
        let month = result.month();
        let day = result.date();

        result = moment.utc(Date.UTC(year, month, day, 23, 59, 59));

        return result.toISOString();
    }

    const setDateToBeginningOfDay = (date) => {

        let result = moment.utc(date);

        let year = result.year();
        let month = result.month();
        let day = result.date();

        result = moment.utc(Date.UTC(year, month, day, 0, 0, 0));

        return result.toISOString();
    }

    const setAllDay = (slider) => {

        let newEvent = Object.assign({}, calendarEvent);
        newEvent.allDay = slider;
        if (slider) {
            newEvent.start = setDateToBeginningOfDay(newEvent.start);
            newEvent.end = setDateToLastSecondOfDay(newEvent.end ?? newEvent.start);
        }
        else {
            newEvent.start = originalState.start;
            newEvent.end = originalState.end;
        }

        setEvent(newEvent);
    }

    const setTitle = (text) => {
        let newEvent = Object.assign({}, calendarEvent);
        newEvent.title = text;
        setEvent(newEvent);
    }

    const setDescription = (text) => {
        let newEvent = Object.assign({}, calendarEvent);
        newEvent.description = text;
        setEvent(newEvent);
    }

    const setLocation = (text) => {
        let newEvent = Object.assign({}, calendarEvent);
        newEvent.location = text;
        setEvent(newEvent);
    }

    const toggleRecurrenceDay = (dayKey) => {
        let newEvent = Object.assign({}, calendarEvent);
        const days = Array.isArray(newEvent.recurrenceDays) ? [...newEvent.recurrenceDays] : [];
        const index = days.indexOf(dayKey);
        if (index === -1) {
            days.push(dayKey);
        } else {
            days.splice(index, 1);
        }
        newEvent.recurrenceDays = days;
        setEvent(newEvent);
    };

    const setRecurrenceEnd = (value) => {
        let newEvent = Object.assign({}, calendarEvent);
        newEvent.recurrenceEnd = value || null;
        setEvent(newEvent);
    };

    const stopEditDates = () => {
        setStartOpen(false);
        setEndOpen(false);
    }

    const editStartDate = () => {
        setStartOpen(true);
        setEndOpen(false);
    }

    const editEndDate = () => {
        setStartOpen(false);
        setEndOpen(true);
    }

    const revert = () => {
        setEvent(originalState);
    }

    const handleClose = () => {
        if (canSave()) {
            revert();
        }
        props.handleCancel();
    }

    const handleSave = () => {
        let eventToSave = Object.assign({}, calendarEvent);

        const inverseWeekdayMap = {
            Mon: 'monday',
            Tue: 'tuesday',
            Wed: 'wednesday',
            Thu: 'thursday',
            Fri: 'friday',
            Sat: 'saturday',
            Sun: 'sunday'
        };

        const recurrenceDays = Array.isArray(calendarEvent.recurrenceDays) ? calendarEvent.recurrenceDays : [];

        const extended = Object.assign({}, eventToSave.extendedProps);

        if (recurrenceDays.length > 0) {
            extended.byWeekDays = recurrenceDays
                .map(d => inverseWeekdayMap[d])
                .filter(Boolean);
        }
        else {
            extended.byWeekDays = null;
        }

        if (calendarEvent.recurrenceEnd) {
            // Treat the selected "repeat until" date as inclusive by
            // setting untilUtc to the end of that day in UTC.
            const until = moment.utc(calendarEvent.recurrenceEnd, "YYYY-MM-DD").endOf('day').toISOString();
            extended.untilUtc = until;
        }
        else {
            extended.untilUtc = null;
        }

        if (!extended.eventType) {
            extended.eventType = 'standard';
        }

        eventToSave.extendedProps = extended;

        props.handleSave(eventToSave);
    }

    const handleDeleteSeries = () => {
        props.handleDelete(calendarEvent, { mode: 'entire-series' });
    };

    const handleDeleteThisAndFuture = () => {
        props.handleDelete(calendarEvent, { mode: 'this-and-future' });
    };

    const handleCancelOccurrence = () => {
        if (props.handleCancelOccurrence) {
            props.handleCancelOccurrence(calendarEvent);
        }
    };

    const humanizeDurationFromEvent = (event) => {

        if (!fetchComplete) {
            return "Calculating...";
        }

        let start = {}, end = {};

        if (event.start instanceof Date) {
            start = DateTime.fromJSDate(event.start);
        }
        else if (event.start['_isAMomentObject']) {
            start = DateTime.fromISO(event.start.toISOString());
        }
        else {
            start = DateTime.fromISO(event.start);
        }

        if (event.end) {
            if (event.end instanceof Date) {
                end = DateTime.fromJSDate(event.end);
            }
            else if (event.end['_isAMomentObject']) {
                end = DateTime.fromISO(event.end.toISOString());
            }
            else {
                end = DateTime.fromISO(event.end);
            }

            let duration = end.diff(start);

            if (event.allDay) {
                const wholeNumberOfDays = Math.ceil(duration.milliseconds / duration.matrix.days.milliseconds);
                return Humanizer(wholeNumberOfDays * duration.matrix.days.milliseconds, { round: true });
            }

            return Humanizer(duration.milliseconds, { round: true });
        }

        return Humanizer(0, { round: true, units: ["m"] });
    }

    return (
        <>
            {props.show ?
                <Modal
                    show={props.show}
                    onHide={props.handleCancel}
                    backdrop="static"
                    keyboard={true}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>{fetchComplete && props.standardEventId !== -1 ? "Edit" : "New"} Event Details</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>

                        <div className="row mb-12">
                            <label className="form-label col-form-label col-md-2">Title</label>
                            <div className="col-md-10">
                                <input type="text" className="form-control" placeholder="Enter Title" value={calendarEvent.title || ""} onChange={e => setTitle(e.target.value)} />
                            </div>
                        </div>
                        <div className="row mb-12">
                            <label className="form-label col-form-label col-md-2">Description</label>
                            <div className="col-md-10">
                                <textarea className="form-control" rows="3" placeholder="Enter Description" value={calendarEvent.description || ""} onChange={e => setDescription(e.target.value)} />
                            </div>
                        </div>
                        <div className="row mb-12">
                            <label className="form-label col-form-label col-md-2">Location</label>
                            <div className="col-md-10">
                                <input type="text" className="form-control" placeholder="Enter Location" value={calendarEvent.location || ""} onChange={e => setLocation(e.target.value)} />
                            </div>
                        </div>
                        {companies.length > 1 && (
                            <div className="row mb-12">
                                <label className="form-label col-form-label col-md-2">Company</label>
                                <div className="col-md-10">
                                    <select
                                        className="form-control"
                                        value={(calendarEvent.extendedProps && calendarEvent.extendedProps.companyId) || ""}
                                        onChange={e => {
                                            const value = e.target.value;

                                            let newEvent = Object.assign({}, calendarEvent);
                                            const extended = Object.assign({}, newEvent.extendedProps);

                                            if (value) {
                                                extended.companyId = value;
                                            } else {
                                                delete extended.companyId;
                                            }

                                            newEvent.extendedProps = extended;
                                            setEvent(newEvent);
                                        }}
                                    >
                                        <option value="">Select Company</option>
                                        {companies.map(c => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}
                        <div className="row mb-12">
                            <label className="form-label col-form-label col-md-2">Recurring?</label>
                            <div className="col-md-10 pt-2">
                                <div className="form-check form-switch">
                                    <label className="form-check-label" htmlFor="isRecurring"></label>
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="isRecurring"
                                        checked={!!isRecurring}
                                        onChange={e => {
                                            const enabled = e.target.checked;
                                            setIsRecurring(enabled);

                                            if (!enabled) {
                                                let newEvent = Object.assign({}, calendarEvent);
                                                newEvent.recurrenceDays = [];
                                                newEvent.recurrenceEnd = null;
                                                setEvent(newEvent);
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        {isRecurring && (
                            <>
                                <div className="row mb-12">
                                    <label className="form-label col-form-label col-md-2">Repeat</label>
                                    <div className="col-md-10 pt-2">
                                        {[
                                            { key: 'Mon', label: 'Mon' },
                                            { key: 'Tue', label: 'Tue' },
                                            { key: 'Wed', label: 'Wed' },
                                            { key: 'Thu', label: 'Thu' },
                                            { key: 'Fri', label: 'Fri' },
                                            { key: 'Sat', label: 'Sat' },
                                            { key: 'Sun', label: 'Sun' }
                                        ].map(d => (
                                            <div className="form-check form-check-inline" key={d.key}>
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={`recurrence-${d.key}`}
                                                    checked={(calendarEvent.recurrenceDays || []).includes(d.key)}
                                                    onChange={() => toggleRecurrenceDay(d.key)}
                                                />
                                                <label className="form-check-label" htmlFor={`recurrence-${d.key}`}>{d.label}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="row mb-12">
                                    <label className="form-label col-form-label col-md-2">Repeat Until</label>
                                    <div className="col-md-10">
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={calendarEvent.recurrenceEnd || ""}
                                            onChange={e => setRecurrenceEnd(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                        <div className="row mb-12">
                            <label className="form-label col-form-label col-md-2">Start</label>
                            <div className="col-md-10">
                                <DateTimePicker
                                    fetchComplete={fetchComplete}
                                    openDtp={startOpen}
                                    showTime={!calendarEvent.allDay}
                                    dateValue={calendarEvent.start}
                                    placeholder="Select 'Start' Date"
                                    stopEditDate={stopEditDates}
                                    setDate={setStartDate}
                                    editDate={editStartDate}
                                />
                            </div>
                        </div>

                        <div className="row mb-12">
                            <label className="form-label col-form-label col-md-2">End</label>
                            <div className="col-md-10">
                                <DateTimePicker
                                    fetchComplete={fetchComplete}
                                    openDtp={endOpen}
                                    showTime={!calendarEvent.allDay}
                                    dateValue={calendarEvent.end ?? calendarEvent.start}
                                    placeholder="Select 'End' Date"
                                    stopEditDate={stopEditDates}
                                    setDate={setEndDate}
                                    editDate={editEndDate}
                                />
                            </div>
                        </div>

                        <div className="row mb-12">
                            <label className="form-label col-form-label col-md-2">All Day?</label>
                            <div className="col-md-10 pt-2">
                                <div className="form-check form-switch">
                                    <label className="form-check-label" htmlFor="allDay"></label>
                                    <input className="form-check-input" type="checkbox" id="allDay" checked={!!calendarEvent.allDay} onChange={e => setAllDay(e.target.checked)} />
                                </div>
                            </div>
                        </div>

                        <div className="row mb-12">
                            <label className="form-label col-form-label col-md-2">Duration</label>
                            <div className="col-md-10 ">
                                <input className="form-control" readOnly placeholder="Duration" value={humanizeDurationFromEvent(calendarEvent)} />
                            </div>
                        </div>

                    </Modal.Body>
                    <Modal.Footer>
                        {props.standardEventId !== -1 && fetchComplete && (
                            <>
                                <div className="btn-group me-auto">
                                    <Button
                                        variant="secondary"
                                        className="dropdown-toggle"
                                        data-bs-toggle="dropdown"
                                    >
                                        Actions <span className="caret"></span>
                                    </Button>
                                    <div className="dropdown-menu">
                                        {isRecurring && !calendarEvent.isFirstOccurrence && (
                                            <button
                                                type="button"
                                                className="dropdown-item"
                                                onClick={handleCancelOccurrence}
                                            >
                                                Cancel this occurrence
                                            </button>
                                        )}

                                        {/* First/original occurrence of a recurring series: only delete entire series */}
                                        {isRecurring && calendarEvent.isFirstOccurrence && (
                                            <button
                                                type="button"
                                                className="dropdown-item"
                                                onClick={handleDeleteSeries}
                                            >
                                                Delete entire series
                                            </button>
                                        )}

                                        {/* Later virtual occurrences: allow series delete or this-and-future */}
                                        {isRecurring && !calendarEvent.isFirstOccurrence && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="dropdown-item"
                                                    onClick={handleDeleteSeries}
                                                >
                                                    Delete entire series
                                                </button>
                                                <button
                                                    type="button"
                                                    className="dropdown-item text-danger"
                                                    onClick={handleDeleteThisAndFuture}
                                                >
                                                    Delete this and future events
                                                </button>
                                            </>
                                        )}

                                        {/* Non-recurring events: simple delete */}
                                        {!isRecurring && (
                                            <button
                                                type="button"
                                                className="dropdown-item text-danger"
                                                onClick={handleDeleteSeries}
                                            >
                                                Delete event
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                        <Button variant="secondary" onClick={handleClose}>
                            Close
                        </Button>
                        {canSave() ?
                            <Button disabled={!fetchComplete} variant="primary" onClick={handleSave}>
                                <i className="fa fa-fw fa-save"></i>
                                Save
                            </Button>
                            : <></>
                        }
                    </Modal.Footer>
                </Modal>
                :
                <></>
            }
        </>
    );

}

export default StandardEventModal;